import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { DynamicForm, SearchableSelectOption, SelectOption } from '@/components/form-builder/form-builder.types';
import { getCommitteeFormObject } from '../utils/committee-form-object';
import { CommitteeRepository } from '@/store/committee-state/committee-state.repository';
import { CommitteeService } from '../committee.service';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RefTypeEnum } from '@/types';
import { RefParamsService } from '../../ref-params/ref-params.service';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { ButtonModule } from 'primeng/button';
import { Router } from '@angular/router';
import { ProfileService } from '@/pages/profile/profile.service';
import { BehaviorSubject, Observable, Subject, combineLatest, forkJoin, of } from 'rxjs';
import { committeeSchema } from '@/validations/commitee.validation';
import { distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'sm-add-committee',
  imports: [PageHeaderComponent, FormBuilderComponent, ButtonModule, TranslateModule, AsyncPipe],
  templateUrl: './add-committee.component.html',
  styleUrl: './add-committee.component.scss',
})
export class AddCommitteeComponent implements OnInit, OnDestroy {
  committeeRepository = inject(CommitteeRepository);
  committeeService = inject(CommitteeService);
  refParamsService = inject(RefParamsService);
  profileService = inject(ProfileService);
  messageService = inject(MessageService);
  translate = inject(TranslateService);
  router = inject(Router);

  private destroy$ = new Subject<void>();

  @ViewChild(FormBuilderComponent) formBuilderComponent!: FormBuilderComponent;

  breadcrumb = [
    {
      label: 'settings.committee.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.committee.breadcrumb.committee',
      routerLink: ['/settings', 'committee'],
    },
    {
      label: 'settings.committee.breadcrumb.new',
      routerLink: ['/settings', 'committee', 'add'],
    },
  ];

  //form-object
  committeTypesOptions: BehaviorSubject<SelectOption[]> = new BehaviorSubject<SelectOption[]>([]);
  membersOptions: BehaviorSubject<SearchableSelectOption[]> = new BehaviorSubject<SearchableSelectOption[]>([]);
  tutelleOptions: BehaviorSubject<SearchableSelectOption[]> = new BehaviorSubject<SearchableSelectOption[]>([]);
  paysOptions: SelectOption[] = [];
  formObject: DynamicForm = getCommitteeFormObject({
    store: this.committeeRepository,
    comTypeOptions: this.committeTypesOptions,
    tutelleOptions: this.tutelleOptions,
    paysOptions: this.paysOptions,
    downloadHandler: (alfrescoId: string, fileName?: string, fileExtension?: string) => this.downloadDocument(alfrescoId, fileName, fileExtension),
    isDetailsMode: false,
  });

  // Form validation - reactive observable
  isFormValid$: Observable<boolean> = combineLatest([
    this.committeeRepository.getNestedObservable('dto', 'nom'),
    this.committeeRepository.getNestedObservable('dto', 'decretCreation'),
    this.committeeRepository.getNestedObservable('dto', 'comTypeId'),
    this.committeeRepository.getObservable('comTypeIdSelected'),
    this.committeeRepository.getNestedObservable('dto', 'responsableId'),
    this.committeeRepository.getNestedObservable<string>('dto', 'theAdresse', 'email'),
  ]).pipe(
    map(([nom, decretCreation, comTypeId, comTypeIdSelected, responsableId, email]) => {
      // Check all required fields
      const isEmailValid = email && typeof email === 'string' && email.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
      return !!(nom && decretCreation && comTypeId && comTypeIdSelected && responsableId && isEmailValid);
    })
  );

  ngOnInit() {
    this.reset();
    this.initComTypeListener();
    this.loadInitialData();
    this.formObject = getCommitteeFormObject({
      store: this.committeeRepository,
      comTypeOptions: this.committeTypesOptions,
      tutelleOptions: this.tutelleOptions,
      paysOptions: this.paysOptions,
      memberOptions: this.membersOptions,
      downloadHandler: (alfrescoId: string, fileName?: string, fileExtension?: string) => this.downloadDocument(alfrescoId, fileName, fileExtension),
      isDetailsMode: false,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addCommittee() {
    // Set all touched flags to true
    this.committeeRepository.set('nomTouched', true);
    this.committeeRepository.set('decretCreationTouched', true);
    this.committeeRepository.set('comTypeIdTouched', true);
    this.committeeRepository.set('theAdresseTouched', true);
    this.committeeRepository.set('responsableTouched', true);

    // Validate the data
    const dto = this.committeeRepository.get('dto') as any;
    const result = committeeSchema.safeParse(dto);
    if (!result.success) {
      this.committeeRepository.setErrors(result.error.flatten().fieldErrors);
      return;
    }

    // Validate all fields
    this.committeeRepository.set('nomError', !dto.nom);
    this.committeeRepository.set('decretCreationError', !dto.decretCreation);
    this.committeeRepository.set('comTypeIdError', !this.committeeRepository.get('comTypeIdSelected'));
    this.committeeRepository.set('theAdresseError', !dto.theAdresse);
    this.committeeRepository.set('responsableError', !dto.responsable);
    // Recreate the form object to ensure error observables are fresh

    this.committeeService.addCommittee(this.committeeRepository.get('dto')).subscribe({
      next: (response: any) => {
        this.committeeRepository.reset();
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.committee.message.success.added'),
        });
        this.router.navigate(['/settings', 'committee', 'update', response.id]);
      },
      error: (response: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: response?.error?.message || this.translate.instant('settings.committee.message.error.added'),
        });
      },
    });
  }

  reset() {
    this.committeeRepository.reset();
    this.setInitialDisabledState();
    this.committeeRepository.set('formSubmitted', false);
    // Restore memberOptions after reset if they were loaded
    // if (this.membersOptions.getValue().length > 0) {
    //   this.committeeRepository.set('memberOptions', this.membersOptions.getValue());
    // }
  }

  onFieldBlur() {
    if (this.membersOptions.getValue().length > 0) {
      this.committeeRepository.set('memberOptions', this.membersOptions.getValue());
    }
  }

  downloadDocument(alfrescoId: string, fileName?: string, fileExtension?: string) {
    this.committeeService.downloadDocument(alfrescoId).subscribe({
      next: (blob: Blob) => {
        // Create a download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // Use the actual file extension if provided, otherwise default to pdf
        const extension = fileExtension || 'pdf';
        const name = fileName || `document_${alfrescoId}`;
        link.download = `${name}.${extension}`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.committee.message.error.download'),
        });
      },
    });
  }

  private initComTypeListener() {
    this.committeeRepository
      .getObservable<SelectOption>('comTypeIdSelected')
      .pipe(
        takeUntil(this.destroy$),
        map(option => {
          const code = option?.code;
          if (typeof code === 'number') {
            return code;
          }
          const parsed = Number(code);
          return Number.isNaN(parsed) ? 0 : parsed;
        }),
        distinctUntilChanged(),
        tap(() => {
          this.committeeRepository.set('tuttelSelected', undefined);
          this.committeeRepository.setNested('dto', 'tutelId', null);
          this.committeeRepository.setNested('isDisabled', 'tutelId', true);
          // this.tutelleOptions.next([]);
        }),
        switchMap(typeId => (typeId ? this.committeeService.getTutelsWithoutCom(typeId) : of([])))
      )
      .subscribe({
        next: tutelleOptions => {
          const hasData = tutelleOptions.length > 0;
          this.tutelleOptions.next(
            tutelleOptions.map(ref => ({
              name: ref.nom,
              code: ref.id,
              searchable: `${ref.nom}_${ref.abreviation}_${ref.nomAr}`,
            }))
          );
          this.committeeRepository.setNested('isDisabled', 'tutelId', !hasData);
        },
        error: () => {
          this.tutelleOptions.next([]);
          this.committeeRepository.setNested('isDisabled', 'tutelId', true);
        },
      });
  }

  private loadInitialData() {
    forkJoin({
      comTypeOptions: this.refParamsService.getSpecializedRefParams(RefTypeEnum.COMMITEE_TYPE),
      paysOptions: this.refParamsService.getSpecializedRefParams(RefTypeEnum.COUNTRY),
      membersOptions: this.profileService.getUserList(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ comTypeOptions, paysOptions, membersOptions }) => {
        this.committeTypesOptions.next(comTypeOptions.map(refType => ({ name: refType.description, code: refType.id })));
        this.paysOptions = paysOptions.map((p: any) => ({ name: p.description, code: p.id }));
        this.membersOptions.next(
          membersOptions.map(user => ({
            name: `${user.firstName} ${user.lastName}`,
            code: user.id,
            searchable: `${user.email}_${user.firstName}_${user.lastName}_${user.username}`,
          }))
        );
        this.committeeRepository.set('comTypeOptions', this.committeTypesOptions.getValue());
        this.committeeRepository.set('memberOptions', this.membersOptions.getValue());
      });
  }

  private setInitialDisabledState(disableTutelle = true) {
    this.committeeRepository.setNested('isDisabled', {
      nom: false,
      abreviation: false,
      nomAr: false,
      decretCreation: false,
      responsable: false,
      special: false,
      adresse: false,
      contact: false,
      ancrage: false,
      comTypeId: false,
      members: false,
      tutelId: disableTutelle,
      theAdresse: false,
      files: false,
    });
  }
}
